import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Wand2, TrendingUp, Check, Info } from 'lucide-react';
import type { AutoFillSuggestion } from '@/services/aiService';
import type { Project } from '@/types/timesheet';

interface SmartAutoFillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  suggestions: AutoFillSuggestion[];
  projects: Project[];
  onApply: (suggestions: AutoFillSuggestion[]) => void;
}

export const SmartAutoFillDialog: React.FC<SmartAutoFillDialogProps> = ({
  open,
  onOpenChange,
  suggestions,
  projects,
  onApply,
}) => {
  const [selectedSuggestions, setSelectedSuggestions] = React.useState<Set<string>>(
    new Set(suggestions.map(s => s.entryId))
  );

  React.useEffect(() => {
    setSelectedSuggestions(new Set(suggestions.map(s => s.entryId)));
  }, [suggestions]);

  const toggleSuggestion = (entryId: string) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedSuggestions(newSelected);
  };

  const handleApply = () => {
    const selected = suggestions.filter(s => selectedSuggestions.has(s.entryId));
    onApply(selected);
    onOpenChange(false);
  };

  const getProjectName = (projectId: string) => {
    return projects.find(p => p.id === projectId)?.name || 'Unknown Project';
  };

  const getTotalHours = (hours: Record<string, number>) => {
    return Object.values(hours).reduce((sum, h) => sum + h, 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Smart Auto-Fill Suggestions
          </DialogTitle>
          <DialogDescription>
            Based on your recent patterns, we've prepared these timesheet entries for you.
            Select which ones to apply.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-1">
        {suggestions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center gap-3">
            <Info className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No suggestions available — we couldn't find a recent week with reusable entries.
              Suggestions will appear once you have prior timesheets with manually entered time.
            </p>
          </div>
        ) : (
        <div className="space-y-3 py-4 px-0.5">
          {suggestions.map((suggestion) => {
            const isSelected = selectedSuggestions.has(suggestion.entryId);
            const totalHours = getTotalHours(suggestion.hours);

            return (
              <Card
                key={suggestion.entryId}
                className={`cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-primary' : 'hover:bg-accent'
                }`}
                onClick={() => toggleSuggestion(suggestion.entryId)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'bg-primary border-primary'
                              : 'border-muted-foreground'
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <h4 className="font-semibold text-sm">
                          {getProjectName(suggestion.projectId)}
                        </h4>
                        <Badge variant="secondary" className="text-xs">
                          {totalHours.toFixed(1)}h/week
                        </Badge>
                      </div>

                      <p className="text-xs text-muted-foreground pl-7">
                        {suggestion.description}
                      </p>

                      <div className="flex items-center gap-2 pl-7">
                        <TrendingUp className="h-3 w-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {suggestion.reason}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {(suggestion.confidence * 100).toFixed(0)}% confidence
                        </Badge>
                      </div>

                      {/* Daily breakdown */}
                      <div className="flex gap-1 pl-7 pt-1">
                        {Object.entries(suggestion.hours).map(([day, hours]) => {
                          if (hours === 0) return null;
                          return (
                            <div
                              key={day}
                              className="text-xs bg-muted px-2 py-1 rounded"
                            >
                              {day.slice(0, 3)}: {hours.toFixed(1)}h
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        )}
        </div>

        <DialogFooter className="shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={selectedSuggestions.size === 0}>
            Apply {selectedSuggestions.size} {selectedSuggestions.size === 1 ? 'Entry' : 'Entries'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
